import {
  AdPlatform,
  ApprovalStatus,
  CopyTigerAdApproval,
  CopyTigerLineField,
  CopyTigerRunApprovals
} from '@blisspointmedia/bpm-types/dist/CopyTiger';
import { Badge } from '../../Components/Badge';
import { Design, Size } from '../../Components/designConstants';
import { formatFriendlyTimestamp } from '../../utils/date-utils';
import { PLATFORM_LABELS } from './copyTigerUtils';
import { adKey, CopyTigerRunAd, RunAdGroup, STATUS_INTENT } from './runReviewUtils';

interface ReadyToApplyProps {
  adGroups: RunAdGroup[];
  getApproval: (key: string) => CopyTigerAdApproval;
  run: CopyTigerRunApprovals['run'];
}

const DECIDED: ApprovalStatus[] = ['approved', 'rejected'];

const STAGES = [
  {
    label: 'Tinuiti',
    status: 'internalStatus',
    reviewer: 'internalReviewer',
    note: 'internalNote'
  },
  { label: 'Client', status: 'clientStatus', reviewer: 'clientReviewer', note: 'clientNote' }
] as const;

const FIELD_LABELS: Record<CopyTigerLineField, string> = {
  headlines: 'Headline',
  descriptions: 'Description'
};

// The generated copy minus every line either stage rejected: what would be pushed for an approved ad.
const getCopyToApply = (
  ad: CopyTigerRunAd,
  approval: CopyTigerAdApproval,
  field: CopyTigerLineField
) =>
  ad.generated[field].filter(
    (_, index) => !approval.lineRejections.some(r => r.field === field && r.index === index)
  );

// Internal view once the client has submitted: the copy ready to go to the platforms, then what goes back
// to the AI for a redraft (client-rejected ads and every rejected line).
export const ReadyToApply = ({ adGroups, getApproval, run }: ReadyToApplyProps) => {
  const ads = adGroups.flatMap(group =>
    group.ads.map(ad => ({
      group,
      ad,
      approval: getApproval(adKey(group.platform, group.accountId, group.adGroupId, ad.adId))
    }))
  );
  const approved = ads.filter(
    ({ approval }) => approval.internalStatus === 'approved' && approval.clientStatus === 'approved'
  );
  const platforms = [...new Set(approved.map(({ group }) => group.platform))];
  const clientRejected = ads.filter(({ approval }) => approval.clientStatus === 'rejected');
  const withRejectedLines = ads.filter(({ approval }) => approval.lineRejections.length > 0);

  // When each stage finished: Tinuiti sending the run, the client submitting their review.
  const stageHandoff = {
    Tinuiti: { by: run.sentBy, at: run.sentToClientAt },
    Client: { by: run.clientSubmittedBy, at: run.clientSubmittedAt }
  };

  return (
    <>
      <h3 className='run-review-section-title'>Ready to send to Google</h3>
      <section className='run-review-group'>
        {STAGES.map(({ label, status, reviewer }) => {
          const reviewers = [
            ...new Set(
              [stageHandoff[label].by, ...ads.map(({ approval }) => approval[reviewer])].filter(
                (name): name is string => !!name
              )
            )
          ];
          return (
            <div key={label} className='run-review-ad-header'>
              <span className='run-review-ad-id'>{label}</span>
              {DECIDED.map(decision => (
                <Badge key={decision} size={Size.SM} design={STATUS_INTENT[decision]}>
                  {`${ads.filter(({ approval }) => approval[status] === decision).length} ${decision}`}
                </Badge>
              ))}
              {reviewers.length > 0 && (
                <span className='run-review-group-ids'>
                  {`Reviewed by ${reviewers.join(', ')}`}
                  {stageHandoff[label].at &&
                    ` · ${formatFriendlyTimestamp(stageHandoff[label].at)}`}
                </span>
              )}
            </div>
          );
        })}
      </section>
      {platforms.length === 0 && (
        <div className='run-review-ad-note'>
          No ads were approved by both Tinuiti and the client.
        </div>
      )}
      {platforms.map(platform => {
        const platformAds = approved.filter(({ group }) => group.platform === platform);
        return (
          <section key={platform} className='run-review-group'>
            <div className='run-review-group-header open'>
              <Badge size={Size.SM} design={Design.NEUTRAL}>
                {PLATFORM_LABELS[platform as AdPlatform] ?? platform}
              </Badge>
              <span className='run-review-group-ids'>{`${platformAds.length} ad${platformAds.length === 1 ? '' : 's'}`}</span>
            </div>
            <div className='run-review-group-body'>
              {platformAds.map(({ group, ad, approval }) => (
                <div key={ad.adId} className='run-review-ad'>
                  <div className='run-review-ad-header'>
                    <span className='run-review-ad-id'>{`Ad ${ad.adId}`}</span>
                    <span className='run-review-group-ids'>
                      {`${group.adGroupName ?? `Ad group ${group.adGroupId}`} · Account ${group.accountId}`}
                    </span>
                  </div>
                  <div className='run-review-copy'>
                    {(['headlines', 'descriptions'] as const).map(field => {
                      const lines = getCopyToApply(ad, approval, field);
                      return (
                        <div key={field} className='run-review-copy-list'>
                          <div className='run-review-copy-label'>
                            {`${FIELD_LABELS[field]}s (${lines.length})`}
                          </div>
                          <ul className='run-review-copy-lines'>
                            {lines.map(line => (
                              <li key={line} className='run-review-copy-line'>
                                {line}
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                  <div className='run-review-ad-footer'>
                    {STAGES.map(({ label, reviewer, note }) => (
                      <span key={label} className='run-review-footer-label'>
                        {`${label}: ${approval[reviewer] ?? 'reviewer unknown'}`}
                        {approval[note] && (
                          <span className='run-review-ad-note'>{` "${approval[note]}"`}</span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        );
      })}

      <h3 className='run-review-section-title'>Back to AI for redraft</h3>
      {clientRejected.length === 0 && withRejectedLines.length === 0 ? (
        <div className='run-review-ad-note'>Nothing was rejected.</div>
      ) : (
        <section className='run-review-group'>
          {clientRejected.map(({ ad, approval }) => (
            <div key={`ad-${ad.adId}`} className='run-review-ad-header'>
              <span className='run-review-ad-id'>{`Ad ${ad.adId}`}</span>
              <Badge size={Size.SM} design={Design.NEUTRAL}>
                Rejected by client
              </Badge>
              {approval.clientNote && (
                <span className='run-review-ad-note'>{approval.clientNote}</span>
              )}
            </div>
          ))}
          {withRejectedLines.map(({ ad, approval }) =>
            approval.lineRejections.map(rejection => (
              <div
                key={`line-${ad.adId}-${rejection.stage}-${rejection.field}-${rejection.index}`}
                className='run-review-ad-header'
              >
                <span className='run-review-ad-id'>{`Ad ${ad.adId}`}</span>
                <Badge size={Size.SM} design={Design.NEUTRAL}>
                  {`${FIELD_LABELS[rejection.field]} rejected by ${rejection.stage === 'internal' ? 'Tinuiti' : 'client'}`}
                </Badge>
                <span>{rejection.text}</span>
                {rejection.note && <span className='run-review-ad-note'>{rejection.note}</span>}
              </div>
            ))
          )}
        </section>
      )}
    </>
  );
};
